import { useAccessControl, useCan } from '@skewb/access-control/react';
import type { ReactNode } from 'react';

interface CanProps {
  action: string;
  subject: string;
  /**
   * Extra `(action, subject)` pairs that also grant access — renders if the primary pair
   * OR any of these is permitted. Omit for the common single-pair case.
   */
  anyOf?: { action: string; subject: string }[];
  /** Rendered when the ability does NOT permit the capability. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Inline render-gate for action affordances (buttons, links, menu items): renders
 * `children` only when the ability permits `(action, subject)` (or any pair in `anyOf`),
 * otherwise renders `fallback` (nothing by default).
 *
 * Use this to HIDE a control that leads to a capability the user lacks — so they never
 * click through to a 403. It is UX-only and does NOT replace the route/page guard
 * ({@link RequireAbility}) or the backend guard, which remain the real boundary for anyone
 * who reaches the URL directly. While the ability is still hydrating, nothing is rendered
 * (deny-until-loaded), matching the fail-closed posture.
 */
export function Can({ action, subject, anyOf, fallback = null, children }: CanProps) {
  const { ability } = useAccessControl();
  const allowed =
    useCan(action, subject) || (anyOf?.some((p) => ability.can(p.action, p.subject)) ?? false);

  return allowed ? <>{children}</> : <>{fallback}</>;
}
